import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
    Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import { UserTypes } from '../../types';

interface ImportResultsModalProps {
    open: boolean;
    onClose: () => void;
    results: UserTypes.ImportExecuteResponse;
}

export const ImportResultsModal: React.FC<ImportResultsModalProps> = ({
    open,
    onClose,
    results,
}) => {
    const { created, teamAssignments, tagsCreated, errors } = results;
    const hasErrors = errors.length > 0;
    const isPartialSuccess = created > 0 && hasErrors;
    const isCompleteSuccess = created > 0 && !hasErrors;
    const isCompleteFailure = created === 0 && hasErrors;

    const downloadErrorReport = () => {
        // Build CSV content
        const headers = ['Row', 'Error Reason'];
        const rows = errors.map(err => [
            (err.rowIndex + 2).toString(), // +1 for 0-based index, +1 for header row
            `"${err.reason.replace(/"/g, '""')}"` // Escape quotes in CSV
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `import-errors-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getStatusIcon = () => {
        if (isCompleteSuccess) return <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />;
        if (isCompleteFailure) return <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />;
        return <WarningAmberIcon color="warning" sx={{ fontSize: 48 }} />;
    };

    const getStatusTitle = () => {
        if (isCompleteSuccess) return 'Import Successful';
        if (isCompleteFailure) return 'Import Failed';
        return 'Import Completed with Errors';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getStatusIcon()}
                    <Typography variant="h6" component="span">
                        {getStatusTitle()}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Success Stats */}
                {created > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>{created}</strong> user{created !== 1 ? 's' : ''} imported successfully
                            {teamAssignments > 0 && (
                                <> · <strong>{teamAssignments}</strong> assigned to teams</>
                            )}
                            {tagsCreated > 0 && (
                                <> · <strong>{tagsCreated}</strong> new tag{tagsCreated !== 1 ? 's' : ''} created</>
                            )}
                        </Typography>
                    </Alert>
                )}

                {/* No Users Created Message */}
                {created === 0 && !hasErrors && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            No users were imported. All users may already exist in the system.
                        </Typography>
                    </Alert>
                )}

                {/* Errors Section */}
                {hasErrors && (
                    <>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                {errors.length} row{errors.length !== 1 ? 's' : ''} failed to import
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                These rows could not be imported due to conflicts or validation errors.
                            </Typography>
                        </Alert>

                        <Box sx={{ 
                            maxHeight: 200, 
                            overflow: 'auto', 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <List dense disablePadding>
                                {errors.slice(0, 10).map((err, idx) => (
                                    <React.Fragment key={idx}>
                                        <ListItem sx={{ py: 1, px: 2 }}>
                                            <ListItemText
                                                primary={`Row ${err.rowIndex + 2}`}
                                                secondary={err.reason}
                                                primaryTypographyProps={{ 
                                                    variant: 'body2', 
                                                    fontWeight: 500,
                                                    color: 'error.main'
                                                }}
                                                secondaryTypographyProps={{ 
                                                    variant: 'caption' 
                                                }}
                                            />
                                        </ListItem>
                                        {idx < Math.min(errors.length, 10) - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>

                        {errors.length > 10 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Showing first 10 of {errors.length} errors. Download the full report for all details.
                            </Typography>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadErrorReport}
                            size="small"
                            fullWidth
                        >
                            Download Error Report (CSV)
                        </Button>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};
